import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  PageWrapper,
  parseLocalDateFromYYYYMMDD,
  STATUSPAGE,
} from '@repo/ui';
import { FormikValues } from 'formik';
import { ReportsGeneralFilters } from 'presentation/app/modules/Reports/components/reports-general-filters';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { Button, Card, Col, Input, Label, Row, Spinner } from 'reactstrap';

import {
  PerformanceReportForProfessionalsDetailedRow,
  PerformanceReportForProfessionalsEvolutionItem,
  PerformanceReportForProfessionalsSummary,
  PerformanceReportForProfessionalsTopValidatedActivitiesItem,
} from '../../../models/performance-report-for-professionals';
import { PerformanceReportResultsTable } from './components/performance-report-results-table';
import { PerformanceSummaryWidgets } from './components/performance-summary-widgets';
import { ValidatedActivitiesEvolutionChart } from './components/validated-activities-evolution-chart';
import { ValidatedActivitiesPerProfessionalChart } from './components/validated-activities-per-professional-chart';

interface IProps {
  total: number;
  currentPage: number;
  rowsPerPage: number;
  statusGet: STATUSPAGE;
  statusGetDashboard: STATUSPAGE;
  statusGetRanking: STATUSPAGE;
  validation: FormikValues;
  statusExport: STATUSPAGE;
  summary: PerformanceReportForProfessionalsSummary;
  detailedList: PerformanceReportForProfessionalsDetailedRow[];
  evolution: PerformanceReportForProfessionalsEvolutionItem[];
  topValidatedActivities: PerformanceReportForProfessionalsTopValidatedActivitiesItem[];
  onSearchClick: () => void;
  onExportClick: () => void;
  onClearFilters: () => void;
  onChangePage: (page: number) => void;
  onTableSearch: (value: string) => void;
  onChangeRowsPerPage: (currentRowsPerPage: number, page: number) => void;
  onSort: (
    selectedColumn: TableColumn<PerformanceReportForProfessionalsDetailedRow>,
    sortDirection: SortOrder,
  ) => void;
}

export const PerformanceReportForProfessionalsView = ({
  total,
  onSort,
  summary,
  evolution,
  statusGet,
  statusGetDashboard,
  statusGetRanking,
  validation,
  currentPage,
  rowsPerPage,
  onChangePage,
  detailedList,
  statusExport,
  onSearchClick,
  onExportClick,
  onTableSearch,
  onClearFilters,
  onChangeRowsPerPage,
  topValidatedActivities,
}: IProps) => {
  return (
    <PageWrapper title="Desempenho dos Profissionais">
      <div className="mb-4">
        <h4 className="mb-1">Desempenho dos Profissionais</h4>
        <p className="text-muted mb-0">
          Acompanhe o desempenho, o engajamento e a qualidade das atividades
          realizadas pelos profissionais.
        </p>
      </div>

      <Card className="mb-4">
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <>
              <Col md={4} lg={2} xs={12} className="mb-3">
                <Label htmlFor="period" className="form-label d-block">
                  Período
                </Label>
                <CustomDateRangePicker
                  initialStartDate={parseLocalDateFromYYYYMMDD(
                    validation.values.starts,
                  )}
                  initialEndDate={parseLocalDateFromYYYYMMDD(
                    validation.values.ends,
                  )}
                  placement="auto"
                  onDateChange={(start, ends) => {
                    validation.setFieldValue(
                      'starts',
                      convertDateToYYYYMMDDFormat(start),
                    );
                    validation.setFieldValue(
                      'ends',
                      convertDateToYYYYMMDDFormat(ends),
                    );
                  }}
                />
              </Col>
              <Col md={3} lg={2} className="mb-3">
                <Label htmlFor="workloadUntil" className="form-label">
                  Carga horária até
                </Label>
                <Input
                  id="workloadUntil"
                  name="workloadUntil"
                  type="number"
                  min="0"
                  placeholder="Ex: 40"
                  value={validation.values.workloadUntil}
                  onChange={(event) => {
                    validation.setFieldValue(
                      'workloadUntil',
                      event.target.value,
                    );
                  }}
                />
              </Col>
            </>
          }
          buttonsChildren={
            <div className="d-flex flex-wrap gap-2 justify-content-end align-content-end">
              <Button
                type="button"
                color="link"
                className="px-0 text-decoration-none"
                onClick={onClearFilters}
              >
                Limpar filtros
              </Button>

              <Button
                type="submit"
                color="light"
                onClick={onSearchClick}
                disabled={statusGet === STATUSPAGE.LOADING}
              >
                {statusGet === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">Carregando...</Spinner>
                ) : (
                  <>
                    Pesquisar
                    <i className="ri-refresh-line ms-2"></i>
                  </>
                )}
              </Button>

              <Button
                type="submit"
                color="primary"
                onClick={onExportClick}
                disabled={statusExport === STATUSPAGE.LOADING}
              >
                {statusExport === STATUSPAGE.LOADING ? (
                  <Spinner size="sm">Exportando...</Spinner>
                ) : (
                  <>
                    Exportar para Excel
                    <i className="ri-file-excel-2-line ms-2"></i>
                  </>
                )}
              </Button>
            </div>
          }
        />
      </Card>

      <div>
        <PerformanceSummaryWidgets
          loading={statusGetDashboard}
          summary={summary}
        />
      </div>

      <Row className="mb-4">
        <Col xl={6} className="mb-4 mb-xl-0">
          <ValidatedActivitiesPerProfessionalChart
            loading={statusGetDashboard}
            topValidatedActivities={topValidatedActivities}
          />
        </Col>

        <Col xl={6}>
          <ValidatedActivitiesEvolutionChart
            loading={statusGetDashboard}
            evolution={evolution}
          />
        </Col>
      </Row>

      <PerformanceReportResultsTable
        total={total}
        onSort={onSort}
        loading={statusGetRanking}
        onSearch={onTableSearch}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onChangePage={onChangePage}
        detailedList={detailedList}
        onChangeRowsPerPage={onChangeRowsPerPage}
      />
    </PageWrapper>
  );
};
